"use client";

import { Suspense, useState } from "react";
import { Link } from "@/i18n/routing";
import { useTranslations } from "next-intl";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import CartDrawer from "@/components/cart/CartDrawer";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { trackEvent } from "@/lib/track";

export default function ForgotPasswordPage() {
  return (
    <Suspense>
      <ForgotPasswordContent />
    </Suspense>
  );
}

function ForgotPasswordContent() {
  const t = useTranslations("auth");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      // Always show success regardless of whether email exists (PII-free analytics)
      trackEvent("forgot_password_submitted");
      setSubmitted(true);
    } catch {
      // Still show success to avoid leaking email existence
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <CartDrawer />
      <main className="min-h-screen bg-brand-cream/30">
        <div className="container-luxury py-16 sm:py-24">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-10">
              <h1 className="font-display text-3xl text-brand-dark mb-2">
                {t("forgotPasswordTitle")}
              </h1>
              <p className="text-sm text-brand-muted">
                {t("forgotPasswordSubtitle")}
              </p>
            </div>

            <div className="bg-white border border-gray-100 shadow-sm p-8">
              {submitted ? (
                <div className="text-center space-y-4 animate-fade-in">
                  <div className="w-16 h-16 mx-auto bg-green-50 rounded-full flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h2 className="font-display text-xl text-brand-dark">
                    {t("resetEmailSentTitle")}
                  </h2>
                  <p className="text-sm text-brand-muted">
                    {t("resetEmailSentMessage")}
                  </p>
                  <Link
                    href="/login"
                    className="inline-block text-xs font-medium uppercase tracking-widest text-brand-gold hover:text-brand-gold-dark transition-colors mt-4"
                  >
                    {t("backToSignIn")}
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6 animate-fade-in">
                  <Input
                    label={t("email")}
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? t("sendingResetLink") : t("sendResetLink")}
                  </Button>

                  <p className="text-xs text-center text-brand-muted">
                    {t("rememberedPassword")}{" "}
                    <Link
                      href="/login"
                      className="text-brand-gold hover:text-brand-gold-dark transition-colors"
                    >
                      {t("backToSignIn")}
                    </Link>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
